import type { Parent, RootContent } from 'mdast';
import remarkGfm from 'remark-gfm';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import {
  type NodeSchema,
  type RawSchema,
  type TextNodeSchema,
} from '../schema';
import { BlockNodes, InlineNodes } from './available-extensions';
import {
  initRawSchema,
  toBlockquoteContent,
  toCodeBlockContent,
  toHeadingContent,
  toImageContent,
  toLinkContent,
  toListContent,
  toOrderedListContent,
  toParagraphContent,
} from './raw-schema-helpers';

function extractText(node: RootContent | undefined): string {
  if (!node) {
    return '';
  }
  switch (node.type) {
    case 'code':
      return node.value || '';
    case 'inlineCode':
      return node.value || '';
    case 'link':
      return (node.children || []).map(extractText).join('');
    case 'text': {
      const textNode = node as Extract<RootContent, { type: 'text' }> & {
        value?: string;
      };
      return textNode.value || '';
    }
    default: {
      const children: RootContent[] | undefined = (node as Parent).children;
      return (children || []).map(extractText).join('');
    }
  }
}

function mapInline(children: RootContent[]): {
  inlineNodes: (string | NodeSchema<InlineNodes> | TextNodeSchema)[];
  blockNodes: NodeSchema<BlockNodes>[];
} {
  const inlineNodes: (string | NodeSchema<InlineNodes> | TextNodeSchema)[] = [];
  const blockNodes: NodeSchema<BlockNodes>[] = [];

  children.map((child) => {
    switch (child.type) {
      case 'text': {
        const value = child.value;
        if (value) {
          inlineNodes.push(value);
        }
        break;
      }
      case 'link': {
        const linkNode = child;
        inlineNodes.push(toLinkContent(extractText(linkNode), linkNode.url));
        break;
      }
      case 'inlineCode': {
        const value = child.value;
        if (value) {
          inlineNodes.push(value);
        }
        break;
      }
      case 'image': {
        const imageNode = child;
        if (imageNode.url) {
          blockNodes.push(toImageContent(imageNode.url));
        }
        break;
      }
      default: {
        const childrenNodes: RootContent[] | undefined = (child as Parent)
          .children;
        if (childrenNodes) {
          const inner = extractText(child);
          if (inner) {
            inlineNodes.push(inner);
          }
        }
      }
    }
  });

  return { inlineNodes, blockNodes };
}

export function initMarkdownSchema(markdown: string): RawSchema {
  const tree = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .parse(markdown || '');

  const blockSchemas: NodeSchema<BlockNodes>[] = [];

  tree.children?.map((node) => {
    switch (node.type) {
      case 'paragraph': {
        const { inlineNodes, blockNodes } = mapInline(node.children || []);
        if (inlineNodes.length) {
          blockSchemas.push(toParagraphContent(inlineNodes));
        }
        blockSchemas.push(...blockNodes);
        break;
      }
      case 'heading': {
        const textValue = extractText(node).trim();
        blockSchemas.push(toHeadingContent(textValue, node.depth || 1));
        break;
      }
      case 'blockquote': {
        const textValue = (node.children || [])
          .map((child) => extractText(child))
          .join('\n')
          .trim();
        blockSchemas.push(toBlockquoteContent(textValue));
        break;
      }
      case 'list': {
        const listNode = node;
        const items: (string | NodeSchema<BlockNodes>)[] = (
          listNode.children || []
        )
          .filter((listItem) => listItem.type === 'listItem')
          .map((listItem) => {
            const paragraph = (listItem.children || []).find(
              (listChild) => listChild.type === 'paragraph'
            );
            if (paragraph) {
              const { inlineNodes, blockNodes } = mapInline(
                paragraph.children || []
              );
              blockSchemas.push(...blockNodes);
              return toParagraphContent(
                inlineNodes.length ? inlineNodes : ['']
              );
            }
            const textValue = extractText(listItem).trim();
            return toParagraphContent(textValue);
          });
        if (listNode.ordered) {
          blockSchemas.push(toOrderedListContent(items));
        } else {
          blockSchemas.push(toListContent(items));
        }
        break;
      }
      case 'code': {
        blockSchemas.push(toCodeBlockContent(node.value || ''));
        break;
      }
      case 'image': {
        if (node.url) {
          blockSchemas.push(toImageContent(node.url));
        }
        break;
      }
      default: {
        // Ignore unsupported nodes
      }
    }
  });

  return initRawSchema(blockSchemas);
}
